import React, { useEffect, useState } from "react"
import { X } from "lucide-react"

interface ToastAction {
    label: string
    onClick: () => void
}

interface ToastProps {
    message: string
    type?: "error" | "success" | "info" | "warning"
    duration?: number
    action?: ToastAction
    onClose: () => void
}

export const Toast: React.FC<ToastProps> = ({ message, type = "info", duration = 4000, action, onClose }) => {
    const [isExiting, setIsExiting] = useState(false)

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsExiting(true)
            setTimeout(onClose, 300) // Wait for exit animation
        }, duration)

        return () => clearTimeout(timer)
    }, [duration, onClose])

    const handleClose = () => {
        setIsExiting(true)
        setTimeout(onClose, 300)
    }

    const bgColors = {
        error: "bg-danger-bg border-danger-border",
        success: "bg-green-50 border-green-200",
        info: "bg-blue-50 border-blue-200",
        warning: "bg-yellow-50 border-yellow-200"
    }

    const textColors = {
        error: "text-danger-hover",
        success: "text-green-800",
        info: "text-blue-800",
        warning: "text-yellow-800"
    }

    const iconColors = {
        error: "text-danger",
        success: "text-green-500",
        info: "text-blue-500",
        warning: "text-yellow-500"
    }

    return (
        <div
            className={`
                fixed top-4 left-1/2 -translate-x-1/2 z-[9999]
                ${bgColors[type]} border rounded-lg shadow-lg
                max-w-md w-full mx-4
                transition-all duration-300 ease-in-out
                ${isExiting ? "opacity-0 -translate-y-2" : "opacity-100 translate-y-0"}
            `}
        >
            <div className="flex items-start gap-3 p-4">
                <span className={`${iconColors[type]} text-lg flex-shrink-0`}>
                    {type === "error" && "⚠"}
                    {type === "success" && "✓"}
                    {type === "info" && "ⓘ"}
                    {type === "warning" && "⚠"}
                </span>
                <p className={`${textColors[type]} text-sm flex-1`}>{message}</p>
                {action && (
                    <button
                        type="button"
                        onClick={() => {
                            action.onClick()
                            handleClose()
                        }}
                        className={`${textColors[type]} text-sm font-semibold underline underline-offset-2 hover:opacity-70 transition-opacity flex-shrink-0`}
                    >
                        {action.label}
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleClose}
                    className={`${textColors[type]} hover:opacity-70 transition-opacity flex-shrink-0`}
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}

interface ToastContainerProps {
    toasts: Array<{ id: string; message: string; type?: "error" | "success" | "info" | "warning"; action?: ToastAction }>
    onRemove: (id: string) => void
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
    return (
        <>
            {toasts.map((toast, index) => (
                <div key={toast.id} style={{ top: `${4 + index * 80}px` }}>
                    <Toast message={toast.message} type={toast.type} action={toast.action} onClose={() => onRemove(toast.id)} />
                </div>
            ))}
        </>
    )
}
